import { useQuery } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { DateTimeDisplay } from '../components/common/DateTimeDisplay';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { usePortalConfig } from '../hooks/usePortalConfig';
import { getRoundDetails } from '../services/reportService';
import type { IGoalScorer } from '../types';

const getNestedValue = (obj: any, path: string, defaultValue: any = null): any => {
  if (!obj || !path) return defaultValue;
  const val = path.split('.').reduce((acc: any, key: string) => {
    if (acc && acc[key] !== undefined) return acc[key];
    return defaultValue;
  }, obj);
  if (typeof val === 'boolean') return val ? 'true' : 'false';
  return val ?? defaultValue;
};

const resolvedColors: Record<string, string> = {
  won: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  lost: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  refunded: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
};

const betStatusColors: Record<string, string> = {
  debit_completed: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  credit_started: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
  credit_completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  refund_completed: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
  debit_failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  credit_failed: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  refund_failed: 'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
  refund_started: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
};

const StatusChip = ({ value, colorMap }: { value: string; colorMap: Record<string, string> }) => {
  const cls = colorMap[value] ?? 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${cls}`}>{value}</span>
  );
};

const FieldCard = ({ id, label, isDate, data }: { id: string; label: string; isDate: boolean; data: any }) => {
  const rawValue = getNestedValue(data, id);
  let content: React.ReactNode;

  if (id === 'resolved') {
    content = rawValue ? (
      <StatusChip value={rawValue} colorMap={resolvedColors} />
    ) : (
      <span className="text-sm font-semibold">-</span>
    );
  } else if (id === 'betStatus') {
    content = rawValue ? (
      <StatusChip value={rawValue} colorMap={betStatusColors} />
    ) : (
      <span className="text-sm font-semibold">-</span>
    );
  } else if (isDate) {
    content = <DateTimeDisplay date={rawValue} className="text-sm font-semibold" />;
  } else {
    content = <p className="text-sm font-semibold break-all">{rawValue ?? '-'}</p>;
  }

  return (
    <div className="border rounded-lg p-3 min-w-[160px] max-w-[280px] shadow-sm bg-card flex flex-col gap-1">
      <p className="text-xs text-muted-foreground">{label}</p>
      {content}
    </div>
  );
};

export const TransactionDetails = () => {
  const { betTxnId } = useParams<{ betTxnId: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const backUrl = (location.state as any)?.from ?? '/transactions';
  const [goalScorersOpen, setGoalScorersOpen] = useState(false);
  const [inputId, setInputId] = useState(betTxnId ?? '');
  const [activeId, setActiveId] = useState(betTxnId ?? '');

  const portalConfig = usePortalConfig();
  const transactionDetailsPage = portalConfig.pages.find((p) => p.id === 'transactionDetails');
  const sections = transactionDetailsPage?.sections ?? [];

  const { data, isLoading, isError } = useQuery({
    queryKey: ['round-details', activeId],
    queryFn: () => getRoundDetails(activeId),
    enabled: !!activeId,
  });

  const handleSearch = () => {
    if (inputId.trim()) setActiveId(inputId.trim());
  };

  if (transactionDetailsPage && !transactionDetailsPage.enabled) {
    return (
      <Layout>
        <div className="p-4">
          <p className="text-destructive">You do not have access to Transaction Details.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <Button variant="ghost" onClick={() => navigate(backUrl)}>
            &larr; Back to Transactions
          </Button>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Enter Bet Transaction ID"
              value={inputId}
              onChange={(e) => setInputId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="w-72 h-9"
            />
            <Button size="sm" onClick={handleSearch}>
              Get Info
            </Button>
          </div>
        </div>

        {isLoading && <p className="text-muted-foreground">Loading...</p>}
        {isError && <p className="text-destructive">Failed to load round details.</p>}

        {data && (
          <div className="space-y-4">
            {sections
              .filter((section) => section.enabled)
              .map((section) => {
                // goalScorers is a special field - rendered as an expand button, not a FieldCard
                const visibleFields = section.fields.filter((f) => f.visible && f.id !== 'goalScorers');
                const goalScorersField =
                  section.id === 'Match_Info'
                    ? section.fields.find((f) => f.id === 'goalScorers' && f.visible)
                    : undefined;

                const hasGoalScorers =
                  !!goalScorersField &&
                  data.goalScorers &&
                  (data.goalScorers.myTeam.length > 0 || data.goalScorers.opponent.length > 0);

                return (
                  <Card key={section.id} className="shadow-md">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base font-bold">{section.title}</CardTitle>
                        {hasGoalScorers && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setGoalScorersOpen((o) => !o)}
                            className="text-xs"
                          >
                            {goalScorersOpen ? 'Hide' : 'View'} Goal Scorers
                          </Button>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-3">
                        {visibleFields.map((field) => (
                          <FieldCard
                            key={field.id}
                            id={field.id}
                            label={field.label}
                            isDate={field.isDate}
                            data={data}
                          />
                        ))}
                      </div>

                      {/* Goal Scorers - inline under Match Info */}
                      {section.id === 'Match_Info' && goalScorersOpen && data.goalScorers && (
                        <div className="mt-4 grid grid-cols-2 gap-6 border-t pt-4">
                          <div>
                            <h4 className="font-semibold mb-2 text-sm">My Team</h4>
                            {data.goalScorers.myTeam.length === 0 ? (
                              <p className="text-muted-foreground text-sm">No goals</p>
                            ) : (
                              <ul className="text-sm space-y-1">
                                {data.goalScorers.myTeam.map((g: IGoalScorer) => (
                                  <li key={g._id} className="flex items-center gap-1">
                                    <span>{g.name ?? g.playerId}</span>
                                    <GoalScorerIdBadge playerId={g.playerId} playerCardId={g.playerCardId} />
                                    <span>({g.time}')</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div>
                            <h4 className="font-semibold mb-2 text-sm">Opponent</h4>
                            {data.goalScorers.opponent.length === 0 ? (
                              <p className="text-muted-foreground text-sm">No goals</p>
                            ) : (
                              <ul className="text-sm space-y-1">
                                {data.goalScorers.opponent.map((g: IGoalScorer) => (
                                  <li key={g._id} className="flex items-center gap-1">
                                    <span>{g.name ?? g.playerId}</span>
                                    <GoalScorerIdBadge playerId={g.playerId} playerCardId={g.playerCardId} />
                                    <span>({g.time}')</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        )}
      </div>
    </Layout>
  );
};

function GoalScorerIdBadge({ playerId, playerCardId }: { playerId: string; playerCardId: string }) {
  const [open, setOpen] = useState(false);
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = useCallback(() => {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = null;
    }
    openTimeout.current = setTimeout(() => setOpen(true), 150);
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (openTimeout.current) {
      clearTimeout(openTimeout.current);
      openTimeout.current = null;
    }
    closeTimeout.current = setTimeout(() => setOpen(false), 150);
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span
          className="cursor-default border-b border-dotted border-muted-foreground/40 hover:border-foreground/60 transition-colors text-muted-foreground text-xs"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          [{playerId}]
        </span>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-2.5 text-xs"
        side="top"
        align="start"
        sideOffset={6}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1">Card ID</p>
        <p className="font-medium">{playerCardId}</p>
      </PopoverContent>
    </Popover>
  );
}
